import type { TemplateImportRow } from "@adpilot/shared";
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { getApiErrorMessage } from "@/lib/api-error";
import { useImportTemplatePortfolioKeywords } from "../api";
import { type ColumnMapping, type KeywordSheet, readKeywordSheet, toImportRows } from "./excel";

/** A file whose keyword column was not recognized; waits for the user's mapping (TP-02). */
export interface PendingExcelFile {
  id: string;
  fileName: string;
  sheet: KeywordSheet;
  mapping: ColumnMapping;
}

const FILE_NAME_MAX = 255;

/**
 * Parses dropped / picked .xlsx files and imports each one into the draft (the API
 * merges and dedupes). `prepare` saves pending keyword edits and returns the draft
 * version to send.
 */
export function useExcelImport(shopId: string, draftId: string, prepare: () => Promise<number>) {
  const { t } = useTranslation("templatePortfolio");
  const { mutateAsync: importKeywords } = useImportTemplatePortfolioKeywords(shopId, draftId);
  const [pending, setPending] = useState<PendingExcelFile[]>([]);
  const [busy, setBusy] = useState(false);

  const upload = useCallback(
    async (fileName: string, rows: TemplateImportRow[]) => {
      if (rows.length === 0) {
        toast.error(t("keywords.import.noRows", { name: fileName }));
        return false;
      }
      try {
        await importKeywords({
          expectedVersion: await prepare(),
          fileName: fileName.slice(0, FILE_NAME_MAX),
          rows,
        });
        return true;
      } catch (error) {
        toast.error(await getApiErrorMessage(error));
        return false;
      }
    },
    [importKeywords, prepare, t],
  );

  const addFiles = useCallback(
    async (files: readonly File[]) => {
      setBusy(true);
      try {
        // Sequential: every import bumps the draft version the next one must send.
        for (const file of files) {
          let sheet: KeywordSheet;
          try {
            sheet = await readKeywordSheet(file);
          } catch {
            toast.error(t("keywords.import.readFailed", { name: file.name }));
            continue;
          }
          if (sheet.mapping.keyword === undefined) {
            const entry = { id: crypto.randomUUID(), fileName: file.name, sheet };
            setPending((current) => [...current, { ...entry, mapping: sheet.mapping }]);
            continue;
          }
          await upload(file.name, toImportRows(sheet, sheet.mapping));
        }
      } finally {
        setBusy(false);
      }
    },
    [t, upload],
  );

  const setMapping = useCallback((id: string, mapping: ColumnMapping) => {
    setPending((current) => current.map((file) => (file.id === id ? { ...file, mapping } : file)));
  }, []);

  const dismiss = useCallback((id: string) => {
    setPending((current) => current.filter((file) => file.id !== id));
  }, []);

  const applyMapping = useCallback(
    async (file: PendingExcelFile) => {
      setBusy(true);
      try {
        if (await upload(file.fileName, toImportRows(file.sheet, file.mapping))) dismiss(file.id);
      } finally {
        setBusy(false);
      }
    },
    [dismiss, upload],
  );

  return { pending, busy, addFiles, setMapping, dismiss, applyMapping };
}
